import { makeRequest, CommonRequestOptions } from './makeRequest';

export const OBJECT_TYPES = ['courseGroups', 'manageSections'] as const;
export type PermissionObjectType = (typeof OBJECT_TYPES)[number];

export const PERMISSION_LEVELS = ['none', 'view', 'edit', 'assign', 'comment', 'manage'] as const;
export type PermissionLevel = (typeof PERMISSION_LEVELS)[number];

export type ObjectIdType = number | string | null;

export interface EntityDisplayPermissions {
   users: UserPermission[];
   teams: TeamPermission[];
   roleTeams: RoleTeamPermission[];
}

export interface UserPermission extends EntityPermission {
   user: UserBlurb;
}

export interface TeamPermission extends EntityPermission {
   team: TeamBlurb;
}

export interface RoleTeamPermission extends EntityPermission {
   roleTeamName: string;
}

export interface EntityPermission {
   permissions: EntityDisplayPermission;
}

export interface UserBlurb extends CommonBlurb {
   userid: number;
   username: string;
}

export interface TeamBlurb extends CommonBlurb {
   teamid: number;
   name: string;
}

export interface CommonBlurb {
   profileUrl: string;
   avatarUrl: string;
}

export interface EntityDisplayPermission {
   effectivePermission: PermissionLevel;
   directlyGrantedPermission: PermissionLevel;
   inheritedPermissions: InheritedPermission[];
}

export interface InheritedPermission {
   token: PermissionToken;
   permission: PermissionLevel;
}

export type PermissionToken = CourseToken;

export interface CourseToken {
   wikiid: number;
}

export async function getPermissionLevels(
   objectType: PermissionObjectType,
   objectId: ObjectIdType,
   options: CommonRequestOptions
): Promise<PermissionLevel[]> {
   return objectId
      ? await makeRequest('get', `permissions/${objectType}/subject/${objectId}`, options)
      : await makeRequest('get', `permissions/${objectType}/subject`, options);
}

export async function getPermissions(
   objectType: PermissionObjectType,
   objectid: ObjectIdType,
   options: CommonRequestOptions
): Promise<EntityDisplayPermissions> {
   return await makeRequest('get', `permissions/${objectType}/entity/${objectid}`, options);
}

export async function setUserPermission(
   objectType: PermissionObjectType,
   objectId: ObjectIdType,
   userids: number[],
   permissionLevel: PermissionLevel,
   options: CommonRequestOptions
): Promise<void> {
   return await makeRequest('put', `permissions/${objectType}/user/${objectId}`, {
      ...options,
      body: {
         userids,
         displayPermission: permissionLevel,
      },
   });
}

export async function setTeamPermission(
   objectType: PermissionObjectType,
   objectId: ObjectIdType,
   teamids: number[],
   permissionLevel: PermissionLevel,
   options: CommonRequestOptions
): Promise<void> {
   return await makeRequest('put', `permissions/${objectType}/team/${objectId}`, {
      ...options,
      body: {
         teamids,
         displayPermission: permissionLevel,
      },
   });
}
